<template>
  <div
    :style="currentState === STATES.loading ? null : featurePosition.style.value"
    :class="{'absolute-cover flex-center': currentState === STATES.loading}"
  >

    <core-busy-dots v-if="currentState === STATES.loading" />

    <!--is it playable-->
    <core-media-player
      v-else-if="currentState === STATES.playable"
      class="f-file_detail_video-video"
      :src="videoSrc"
      :poster-src="posterSrc"
      :thumbnail-src="thumbnailSrc"
      :screen-caps-count="file.screen_caps_count"
      :name="file.name"
      :width="featurePosition.style.value.width"
      :height="featurePosition.style.value.height"
      :is-active="isActive && !isTransitioning"
      ref="videoPlayer"
      @error="onVideoError"
      @full-screen-change="onFullScreenChange"
    />

    <!--otherwise (processing, errored, need plan)-->
    <file-detail-non-viewable v-else-if="showNonPlayableState" class="f-file_detail_video-unplayable">

      <template #image>
        <div
          :class="{
            'cursor-pointer': currentState.action
          }"
          @click="currentState.action"
        >
          <!--if need plan we want to display screen cap thumb here-->
          <div v-if="currentState.showThumb" class="relative my-0 mx-auto w-40 sm:w-64 md:w-80">
            <img :src="thumbnailSrc" class="border-white border-2 rounded">
            <u-icon v-if="currentState.icon" :name="currentState.icon" class="text-5xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white/50 py-px px-1.5 rounded-full" />
          </div>

          <!--otherwise, we just display icon-->
          <div v-else>
            <u-icon :name="currentState.icon" class="text-5xl text-white" :class="currentState.spin ? 'animate-spin' : null" />
          </div>
        </div>
      </template>

      <template #description>
        <h3>{{currentState.title}}</h3>
        <p v-if="file.user" v-html="currentState.desc" />

        <u-button v-if="currentState.action" @click="currentState.action" class="my-6" size="xl">See What's Included</u-button>
        <p v-if="currentState.showDownload" class="!text-sm italic opacity-50">
          You can always <a href="#" @click.prevent.stop="downloadVideo">download this video</a> and watch it on your computer.
        </p>
      </template>

    </file-detail-non-viewable>

    <file-capture-frame-preview
      v-model:frame-src="frameSrc"
      :file="capturedFile"
      :is-portrait="file.height > file.width"
      @open-file="openCapturedFrame"
    />
  </div>
</template>

<script setup>
  const fileContext = inject('fileContext');
  const emit = defineEmits(['full-screen-change', 'download']);
  const router = useRouter();
  const route = useRoute();
  const buildRoute = useBuildRoute();
  const props = defineProps({
    file: Object,
    availableSpace: Object,
    isActive: Boolean,
    isTransitioning: Boolean
  });

  const filesStore = useFilesStore();

  const videoPlayer = ref();
  const isPlaybackErrored = ref(false);
  const {STATES, currentState, showNonPlayableState, isPlayable} = useVideoState({
    file: computed(() => props.file),
    isPlaybackErrored
  });
  const captureVideoFrameEvent = useEventBus('file-detail-capture-video-frame');
  const videoSrc = computed(() => props.file.preview_url || props.file.download_items[0].path);
  const posterSrc = computed(() => updateQueryParameters(props.file.images.preview, {rotate: null}));
  const thumbnailSrc = computed(() => updateQueryParameters(props.file.images.preview, {
    height: 512,
    width: 512,
    crop: 'fit'
  }));

  const featurePosition = useDetailFeaturePosition({
    featureDimensions: computed(() => {
      if (isPlayable.value) {
        return {
          width: Math.max(props.file.width, VIDEO_MIN_WIDTH),
          height: Math.max(props.file.height, VIDEO_MIN_HEIGHT)
        };
      }

      return {
        width: props.availableSpace.width,
        height: props.availableSpace.height
      };
    }),
    availableSpace: computed(() => props.availableSpace)
  });

  async function convert() {
    if (props.file.is.processing) {
      return;
    }

    const params = {
      fileId: props.file.id,
      userId: props.file.user_id || props.file.user?.id || fileContext.userId,
      shareToken: fileContext.shareToken
    };

    switch (fileContext.contextType) {
      case 'album':
        params.albumId = fileContext.contextId;
        break;
      case 'tag':
        params.tagId = fileContext.contextId;
        break;
      case 'container':
        params.containerId = fileContext.contextId;
        break;
    }

    // Immediately leave the non-rendering needsTranscoding state while the
    // backend starts the job. This also prevents duplicate requests.
    props.file.is.processing = true;

    try {
      await useFileApi().transcodeVideoFile(params);

      //re-fetch the file
      await filesStore.getFileDetails({files: [props.file], refresh: true, ...fileContext});
    } catch (error) {
      props.file.is.processing = false;
      isPlaybackErrored.value = true;
      throw error;
    }
  }

  function onVideoError() {
    isPlaybackErrored.value = true;
  }
  function onFullScreenChange(inFullScreen) {
    emit('full-screen-change', inFullScreen);
  }

  function downloadVideo() {
    emit('download', props.file);
  }

  const { frameSrc, capturedFile, captureVideoFrame: _captureVideoFrame } = useCaptureVideoFrame({
    file: toRef(props, 'file'),
    fileContext
  });

  async function captureVideoFrame() {
    if (!props.isActive) {
      return;
    }

    await _captureVideoFrame(videoPlayer.value.videoEl);
  }

  function openCapturedFrame({file}) {
    const resolvedRoute = router.resolve(buildRoute.toFile({
      file,
      userId: file.user?.slug || file.user?.id || fileContext.userId,
      shareToken: fileContext.shareToken,
      query: route.query
    }));

    window.open(resolvedRoute.href, '_blank', 'noopener,noreferrer');
  }

  watch(() => props.isActive, () => {
    frameSrc.value = '';
    capturedFile.value = null;
  });

  watch([currentState, () => props.isActive], ([newState, isActive]) => {
    //auto trigger convert if the video needs to be transcoded
    if (isActive && newState === STATES.needsTranscoding) {
      convert();
    }
  }, {immediate: true});

  onMounted(() => {
    captureVideoFrameEvent.on(captureVideoFrame);
  });
  onUnmounted(() => {
    captureVideoFrameEvent.off(captureVideoFrame);
  });

</script>
